import {
  primaryMain,
  orange800,
  neutral900,
  white,
  lead600,
  lead800,
  surfaceCard,
  borderSubtle,
  ENABLE_WARM_WASH,
  warmWashBackgroundImage,
  layoutCardRadius,
  layoutButtonPillRadius,
} from './colors';

export const componentOverrides = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        margin: 0,
        backgroundColor: '#FEF6EF',
        ...(ENABLE_WARM_WASH && {
          backgroundImage: warmWashBackgroundImage,
        }),
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: primaryMain,
        textDecoration: 'none',
        '&:hover': {
          color: primaryMain,
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: 'Nunito',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 'normal',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        margin: '4px',
        padding: '8px 16px',
      },
    },
    variants: [
      {
        props: { variant: 'outlined' },
        style: {
          color: primaryMain,
          borderColor: primaryMain,
          '&:hover': {
            backgroundColor: 'rgba(248, 150, 62, 0.1)',
            borderColor: primaryMain,
          },
        },
      },
      {
        props: { variant: 'contained' },
        style: ({ theme }) => ({
          backgroundColor: primaryMain,
          color: white,
          boxShadow: theme.shadows[2],
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            backgroundColor: orange800,
            boxShadow: theme.shadows[4],
          },
          '&:active': {
            boxShadow: theme.shadows[8],
          },
        }),
      },
      {
        props: { variant: 'contained', color: 'secondary' },
        style: ({ theme }) => ({
          backgroundColor: neutral900,
          color: white,
          boxShadow: theme.shadows[2],
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            backgroundColor: lead800,
            boxShadow: theme.shadows[4],
          },
          '&:active': {
            boxShadow: theme.shadows[8],
          },
        }),
      },
      {
        props: { variant: 'pillContained' },
        style: ({ theme }) => ({
          margin: 0,
          minHeight: 52,
          py: 1.5,
          px: 3,
          borderRadius: layoutButtonPillRadius,
          fontFamily: 'Nunito',
          fontSize: '17px',
          fontWeight: 700,
          lineHeight: 1.25,
          textTransform: 'none',
          boxShadow: theme.shadows[2],
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            boxShadow: theme.shadows[4],
          },
          '&:active': {
            boxShadow: theme.shadows[8],
          },
        }),
      },
      {
        props: { variant: 'pillContained', color: 'primary' },
        style: {
          backgroundColor: primaryMain,
          color: white,
          '&:hover': {
            backgroundColor: orange800,
          },
        },
      },
      {
        props: { variant: 'pillContained', color: 'secondary' },
        style: {
          backgroundColor: neutral900,
          color: white,
          '&:hover': {
            backgroundColor: lead800,
          },
        },
      },
      {
        props: { variant: 'outlinedPill' },
        style: {
          margin: 0,
          minHeight: 52,
          py: 1.5,
          px: 3,
          borderRadius: layoutButtonPillRadius,
          fontFamily: 'Nunito',
          fontSize: '17px',
          fontWeight: 700,
          lineHeight: 1.25,
          textTransform: 'none',
          color: primaryMain,
          borderColor: primaryMain,
          borderWidth: 1,
          borderStyle: 'solid',
          backgroundColor: white,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'rgba(248, 150, 62, 0.1)',
            borderColor: primaryMain,
            boxShadow: 'none',
          },
        },
      },
      {
        props: { variant: 'quantityStepper' },
        style: {
          minWidth: 20,
          minHeight: 20,
          p: 0,
          margin: 0,
          boxShadow: 'none',
          '&:hover, &:active': {
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
        },
      },
    ],
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        '&:hover, &:active': {
          boxShadow: 'none',
        },
      },
    },
    variants: [
      {
        props: { variant: 'subtle' },
        style: {
          bgcolor: white,
          border: `1px solid ${borderSubtle}`,
          borderRadius: layoutCardRadius,
          p: 1,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: white,
            boxShadow: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: lead600,
          },
          '&:hover .MuiSvgIcon-root': {
            color: primaryMain,
          },
        },
      },
      {
        props: { variant: 'deleteItem' },
        style: {
          bgcolor: white,
          border: `1px solid ${borderSubtle}`,
          borderRadius: layoutCardRadius,
          p: 1,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: white,
            boxShadow: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: lead600,
          },
          '&:hover .MuiSvgIcon-root': {
            color: primaryMain,
          },
        },
      },
    ],
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: layoutCardRadius,
          '&:hover fieldset': {
            borderColor: primaryMain,
          },
          '&.Mui-focused fieldset': {
            borderColor: primaryMain,
          },
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: surfaceCard,
        borderRadius: layoutCardRadius,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: layoutCardRadius,
      },
    },
    variants: [
      {
        props: { variant: 'transparent' },
        style: {
          backgroundColor: 'transparent',
        },
      },
    ],
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        pageTitle: 'h1',
        sectionTitle: 'h2',
        storeName: 'p',
      },
    },
  },
};
